import type { BookingSdk } from '@libs/graphql/sdk/booking/booking.sdk';
import type { VendorSdk } from '@libs/graphql/sdk/vendor/vendor.sdk';
import type { WhatsAppTemplateSdk } from '@libs/graphql/sdk/whats-app-template/whats-app-template.sdk';
import type { WhatsAppNotifyService } from '@shared/service/whatsapp-notify.service';

import { fmtDateLong } from './date-format.util';
import { lifecycleMessage, type LifecycleMsgContext, type WaLifecycleCode } from './booking-lifecycle-message';

/**
 * WhatsApp ONE vendor about something that happened on a booking — a partner whose service was
 * replaced or removed, the reseller whose dispute was resolved, the vendor whose early release was
 * paid… Used from pages that don't have that vendor's phone or the booking's details loaded (the
 * admin Finance screen, the review form, a customer page messaging a partner), so it reads both
 * fresh: the vendor's name/phone, the booking's date/ref/customer and its reseller (`{hallName}`).
 *
 * Fields the caller already knows (`ctx`) win over the looked-up ones. BEST-EFFORT: never throws,
 * resolves `false` when nothing was sent — call it after the write has succeeded.
 */
export async function notifyVendorOfBookingEvent(
  deps: { wa: WhatsAppNotifyService; templateSdk: WhatsAppTemplateSdk; vendorSdk: VendorSdk; bookingSdk: BookingSdk },
  a: { vendorId: number | null | undefined; bookingId: number; code: WaLifecycleCode; ctx?: LifecycleMsgContext; userId: number },
): Promise<boolean> {
  if (!a.vendorId || !a.bookingId) return false;
  try {
    const bres = await deps.bookingSdk.list(
      { id: { eq: a.bookingId } }, [{ column: 'id', desc: false }],
      ['id', 'bookingNo', 'eventDate', 'contactName', 'resellerVendorId'], 1, 1);
    const b = ((bres.data ?? []) as Array<{ bookingNo?: string | null; eventDate?: string | null; contactName?: string | null; resellerVendorId?: number | null }>)[0];
    const ids = Array.from(new Set([a.vendorId, b?.resellerVendorId].filter((x): x is number => !!x)));
    const vres = await deps.vendorSdk.list(
      { id: { in: ids } }, [{ column: 'id', desc: false }], ['id', 'name', 'phone'], 1, 10);
    const vendors = (vres.data ?? []) as Array<{ id: number; name?: string | null; phone?: string | null }>;
    const target = vendors.find(v => Number(v.id) === a.vendorId);
    const reseller = vendors.find(v => Number(v.id) === Number(b?.resellerVendorId));
    const phone = (target?.phone ?? '').trim();
    if (!phone) return false;

    const msg = await lifecycleMessage(deps.templateSdk, a.code, {
      customerName: (b?.contactName ?? '').trim() || 'The customer',
      vendorName: target?.name ?? '',
      hallName: reseller?.name ?? target?.name ?? '',
      eventDateLong: fmtDateLong((b?.eventDate ?? '').slice(0, 10)),
      bookingRef: b?.bookingNo || `#${a.bookingId}`,
      ...a.ctx,
    });
    return await deps.wa.notify({
      templateCode: a.code, templateLabel: msg.label, audience: 'Vendor',
      name: target?.name ?? '', phone, body: msg.body, userId: a.userId,
    });
  } catch (err) {
    console.warn(`${a.code} WhatsApp failed`, err);
    return false;
  }
}
